import * as React from 'react';
import * as z from 'zod';
import {
  applyMask,
  caretIndexAfterDigits,
  groupDigits,
  maskCapacity,
  stripToDigits,
} from '@noor/lib';
import { Input } from '../input';
import { useFormFieldContext } from './form-field-context';
import { useFieldAria } from './use-field-aria';
import { deriveFieldAutoComplete } from './derive-auto-complete';

/**
 * Formatted controls follow one contract: RHF state holds the CANONICAL value
 * (E.164 phone string, plain number, bare digit string) so Zod schemas validate
 * data, not presentation; the control owns the formatted display. On change the
 * DOM value and caret are written imperatively — the subsequent controlled
 * re-render produces the same string, so React leaves the caret alone.
 */
function syncInput(input: HTMLInputElement, display: string, caret: number): void {
  input.value = display;
  input.setSelectionRange(caret, caret);
}

type FormattedInputProps = Omit<
  React.ComponentProps<typeof Input>,
  'value' | 'onChange' | 'name' | 'type' | 'inputMode'
>;

/* -------------------------------- Phone ------------------------------- */

export interface FormFieldPhoneProps extends FormattedInputProps {
  /** Fixed dial-code prefix, stored as part of the value. */
  dialCode?: string;
  /** Mask for the national number; `#` = digit, literals must be non-digits. */
  mask?: string;
  /** Browser autocomplete attribute. Defaults to an isolated section (`section-${name} tel`) to prevent cross-field autofilling. */
  autoComplete?: string;
}

/**
 * Masked phone input. Displays `+998 90 123-45-67`; the form value is the
 * normalized `+998901234567` (or `''` when empty), so schemas validate
 * `z.string().regex(/^\+998\d{9}$/)`-style patterns. Pasting a full number
 * with or without the dial code is normalized too.
 */
export function FormFieldPhone({
  dialCode = '+998',
  mask = '## ###-##-##',
  placeholder,
  autoComplete,
  ...props
}: FormFieldPhoneProps) {
  const { field } = useFormFieldContext();
  const aria = useFieldAria();
  const capacity = maskCapacity(mask);
  const dialDigits = stripToDigits(dialCode);
  const resolvedAutoComplete = autoComplete ?? deriveFieldAutoComplete(field.name, 'tel');

  const parseNational = (raw: string): string => {
    // The dial code is part of the display-only prefix. If editing removes
    // part of that prefix (for example, macOS Delete removes the `+` first),
    // do not reinterpret the remaining `998` as a national number. The
    // prefix will be rendered back by `toDisplay`.
    const isIncompleteDialPrefix =
      (raw.startsWith('+') && dialCode.startsWith(raw)) || raw === dialDigits;
    if (isIncompleteDialPrefix) return '';

    const hasDialPrefix = raw.startsWith(dialCode);
    let digits = stripToDigits(hasDialPrefix ? raw.slice(dialCode.length) : raw);
    // A paste can include the prefix even when it is already visible in the
    // input, producing a duplicated dial code after the first one is removed.
    // A national number can legitimately start with those digits, so only
    // strip them when the remaining value exceeds the mask capacity.
    if (digits.length > capacity && digits.startsWith(dialDigits)) {
      digits = digits.slice(dialDigits.length);
    }
    return digits.slice(0, capacity);
  };

  const toDisplay = (national: string): string =>
    national ? `${dialCode} ${applyMask(national, mask)}` : dialCode;

  const stored = typeof field.value === 'string' ? field.value : '';
  const display = toDisplay(parseNational(stored));

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target;
    const caretRaw = input.selectionStart ?? input.value.length;
    const national = parseNational(input.value);
    const caretInsidePrefix =
      input.value.startsWith(dialCode) && caretRaw <= dialCode.length;
    const beforeCaret = caretInsidePrefix
      ? 0
      : Math.min(parseNational(input.value.slice(0, caretRaw)).length, national.length);

    const nextDisplay = toDisplay(national);
    const prefixLength = dialCode.length + 1;
    const caret = national
      ? prefixLength + caretIndexAfterDigits(nextDisplay.slice(prefixLength), beforeCaret)
      : dialCode.length;
    syncInput(input, nextDisplay, caret);
    field.onChange(national ? `${dialCode}${national}` : '');
  };

  return (
    <Input
      {...aria}
      type="tel"
      autoComplete={resolvedAutoComplete}
      name={field.name}
      value={display}
      onChange={handleChange}
      onBlur={field.onBlur}
      ref={field.ref}
      placeholder={placeholder ?? toDisplay('0'.repeat(capacity))}
      {...props}
    />
  );
}

/* -------------------------------- Price ------------------------------- */

/** Stays comfortably inside Number.MAX_SAFE_INTEGER (16 digits). */
const MAX_AMOUNT_DIGITS = 15;

export interface FormFieldPriceProps extends FormattedInputProps {
  /**
   * Trailing unit rendered inside the pill after the amount (e.g. `сум`, `%`).
   * Purely presentational — the form value stays the bare `number`.
   */
  suffix?: React.ReactNode;
  /** Maximum numeric value accepted while typing. */
  maxValue?: number;
}

/**
 * Wrap a `FormField.Price` schema field so an emptied box validates as "no
 * amount given". The control stores `null` when cleared — it cannot store
 * `undefined`, see `FormFieldPrice.handleChange` — and a bare `z.number()`
 * rejects `null` with a type complaint ("expected number, received null")
 * rather than the field's own required message. Collapsing it to `undefined`
 * first lets the wrapped schema answer with the message it already defines.
 *
 * ```ts
 * price: emptyPrice(
 *   z.number({ message: t('priceRequired') }).int().positive(t('pricePositive')),
 * )
 * ```
 */
export function emptyPrice<T extends z.ZodType>(schema: T) {
  return z.preprocess((value) => (value === null ? undefined : value), schema);
}

/**
 * Thousand-separated amount input for non-negative integers (so'm has no
 * minor units). Displays `1 250 000`; the form value is the plain `number`,
 * or `null` once the merchant empties the box — see `handleChange` for why
 * emptying cannot store `undefined`. Wrap the schema field in `emptyPrice()`
 * so `null` validates as "no amount given" rather than a type error.
 * An optional `suffix` renders a trailing unit (`сум`, `%`) inside the pill.
 */
export function FormFieldPrice({ suffix, maxValue, ...props }: FormFieldPriceProps) {
  const { field } = useFormFieldContext();
  const aria = useFieldAria();

  const value =
    typeof field.value === 'number' && Number.isFinite(field.value)
      ? Math.trunc(Math.abs(field.value))
      : null;
  const display = value == null ? '' : groupDigits(String(value));

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target;
    const caretRaw = input.selectionStart ?? input.value.length;
    const digits = stripToDigits(input.value)
      .replace(/^0+(?=\d)/, '')
      .slice(0, MAX_AMOUNT_DIGITS);
    const beforeCaret = Math.min(
      stripToDigits(input.value.slice(0, caretRaw)).length,
      digits.length,
    );

    const nextDisplay = digits ? groupDigits(digits) : '';
    if (maxValue !== undefined && digits && Number(digits) > maxValue) {
      syncInput(input, display, Math.min(caretRaw, display.length));
      return;
    }
    syncInput(input, nextDisplay, caretIndexAfterDigits(nextDisplay, beforeCaret));
    // An emptied box stores `null`, not `undefined`. RHF's `Controller` treats
    // `undefined` as "this field has no value" and serves the field's DEFAULT
    // back in its place — so on an EDIT form (price seeded from the product)
    // deleting the last digit snapped the box straight back to the seeded
    // amount, and the next keystroke appended to a number the merchant thought
    // they had cleared. `null` is a value RHF stores verbatim, so the box stays
    // empty. Schemas still see "no amount": the shared `emptyPrice` preprocess
    // below normalizes it to `undefined` before validation.
    field.onChange(digits ? Number(digits) : null);
  };

  return (
    <Input
      {...aria}
      type="text"
      autoComplete={props.autoComplete ?? 'off'}
      inputMode="numeric"
      name={field.name}
      value={display}
      onChange={handleChange}
      onBlur={field.onBlur}
      ref={field.ref}
      {...(suffix !== undefined
        ? { endIcon: <span className="text-base tracking-tight">{suffix}</span> }
        : {})}
      {...props}
    />
  );
}

/* -------------------------------- Masked ------------------------------ */

export interface FormFieldMaskedProps extends FormattedInputProps {
  /** Display template; `#` = digit slot, literals must be non-digits. */
  mask: string;
}

/**
 * Generic digit-mask input (card numbers, contract ids, …). Displays the
 * masked value; the form value is the bare digit string, so schemas validate
 * `z.string().length(n)`-style rules.
 */
export function FormFieldMasked({ mask, ...props }: FormFieldMaskedProps) {
  const { field } = useFormFieldContext();
  const aria = useFieldAria();
  const capacity = maskCapacity(mask);

  const stored =
    typeof field.value === 'string' ? stripToDigits(field.value).slice(0, capacity) : '';
  const display = applyMask(stored, mask);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target;
    const caretRaw = input.selectionStart ?? input.value.length;
    const digits = stripToDigits(input.value).slice(0, capacity);
    const beforeCaret = Math.min(
      stripToDigits(input.value.slice(0, caretRaw)).length,
      digits.length,
    );

    const nextDisplay = applyMask(digits, mask);
    syncInput(input, nextDisplay, caretIndexAfterDigits(nextDisplay, beforeCaret));
    field.onChange(digits);
  };

  return (
    <Input
      {...aria}
      type="text"
      autoComplete={props.autoComplete ?? 'off'}
      inputMode="numeric"
      name={field.name}
      value={display}
      onChange={handleChange}
      onBlur={field.onBlur}
      ref={field.ref}
      placeholder={props.placeholder ?? mask.replace(/#/g, '0')}
      {...props}
    />
  );
}
